// @ts-strict-ignore
import type {
  dashboardApiEndpoints,
  FetcherOf,
  FilteredDashboardApiEndpointWithParamsKeys,
  QueriesOptions,
  QueryKeyOf,
  ResultOf,
  UseDashboardApiQueriesProps,
} from "@dashboardApi/apis";
import { useQueries } from "@tanstack/react-query";

import { useAppGroupId } from "@services/useAppGroupId";

import { useDashboardApi } from "./useDashboardApi";

/**
 * Based on the endpoint specified, this accepts a dynamic array of arguments for the underlying GET fetcher function,
 * and options for react-query, and converts them into the query key and arguments to useQueries
 *
 * This only works with endpoints that have parameters
 * @template Endpoint the endpoint name. Example: `"/engagement/get_changelogs"`
 * @template GetFetcherFunction an intermediary type to remove duplication in the type, this is the type of the fetcher function for `GET`
 * @template TData this is the return value of `select`, which is inferred from the `options` argument.
 */
export const useDashboardApiQueries = <
  Endpoint extends FilteredDashboardApiEndpointWithParamsKeys,
  GetFetcherFunction extends FetcherOf<"GET", Endpoint>,
  TFnData = ResultOf<"GET", Endpoint>,
  TData = TFnData,
>(
  endpoint: Endpoint,
  {
    params,
    options,
  }: UseDashboardApiQueriesProps<typeof dashboardApiEndpoints, Endpoint, GetFetcherFunction, TFnData, TData>
) => {
  const appGroupId = useAppGroupId();
  const { GET } = useDashboardApi(endpoint);
  const queries: QueriesOptions<typeof dashboardApiEndpoints, Endpoint, GetFetcherFunction, TFnData, TData>[] =
    params.map((p) => {
      return {
        queryKey: [{ cacheKey: endpoint, appGroupId }, ...(p || [])] as unknown as QueryKeyOf<"GET", Endpoint>,
        queryFn: GET,
        ...options,
      };
    }) as unknown as QueriesOptions<typeof dashboardApiEndpoints, Endpoint, GetFetcherFunction, TFnData, TData>[];
  return useQueries({ queries });
};
